import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import type { Row } from '@tanstack/react-table';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { User } from 'types/User.ts';
import { IDelete, IDots, IEdit, IEye, ILocked, IUnlocked } from 'ui/component/Icons.tsx';
import { type DialogConfig, UserDialog } from './UserDialog.tsx';

type Props = {
  row: Row<User>;
  isGroup?: boolean;
  onAction?: (action: 'lock' | 'unlock' | 'delete') => void;
};

export const UserListItemMenu: React.FC<Props> = ({ row, onAction, isGroup = false }) => {
  const { t } = useTranslation();
  const [dialogConfig, setDialogConfig] = React.useState<DialogConfig | null>(null);

  const isEnabled = row.original.isActive;

  const handleLock = () => {
    setDialogConfig({ action: 'lock' });
  };

  const handleUnlock = () => {
    setDialogConfig({ action: 'unlock' });
  };

  const handleDelete = () => {
    setDialogConfig({ action: 'delete' });
  };

  const onDialogClosed = () => {
    const action = dialogConfig?.action;
    setDialogConfig(null);
    if (onAction) onAction(action === 'delete' ? 'delete' : isEnabled ? 'lock' : 'unlock');
  };

  return (
    <Menu>
      {dialogConfig && <UserDialog user={row.original} config={dialogConfig} onSave={onDialogClosed} onClose={onDialogClosed} isOpen={true} />}
      <MenuButton className="flex size-full place-content-center place-items-center" data-table-no-row-events>
        <IDots className="text-gray-400" size={28} data-table-no-row-events />
      </MenuButton>
      <MenuItems className="row-menu" anchor="bottom end">
        <MenuItem>
          <Link className="contents" to={`/${isGroup ? 'group' : 'user'}/${row.original.id}`}>
            <button className="row-menu-item">
              <IEye size={20} />
              {t('actions.view')}
            </button>
          </Link>
        </MenuItem>
        <MenuItem>
          <Link className="contents" to={`/${isGroup ? 'group' : 'user'}/${row.original.id}`}>
            <button className="row-menu-item">
              <IEdit size={20} />
              {t('actions.edit')}
            </button>
          </Link>
        </MenuItem>
        {!isGroup && (
          <MenuItem>
            <button className="!border-0 row-menu-item" onClick={isEnabled ? handleLock : handleUnlock}>
              {isEnabled ? <ILocked size={20} /> : <IUnlocked size={20} />}
              {isEnabled ? t('actions.lock') : t('actions.unlock')}
            </button>
          </MenuItem>
        )}
        {!row.original.deleted_at && (
          <MenuItem>
            <button className="!border-0 row-menu-item" onClick={handleDelete}>
              <IDelete size={20} />
              {t('actions.delete')}
            </button>
          </MenuItem>
        )}
      </MenuItems>
    </Menu>
  );
};
